import React from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import SeoCTA from "../components/SeoCTA";
import { getSeoPageBySlug } from "../data/seoPages";

const SITE_URL = "https://www.subtitletsunami.com";

const EnglishToSpanishSubtitleGenerator = () => {
  const page = getSeoPageBySlug("english-to-spanish-subtitle-generator");

  const pageUrl = `${SITE_URL}${page.canonicalPath}`;

  const faqItems = [
    {
      question: "Can I generate Spanish subtitles from an English video?",
      answer:
        "Yes. SubtitleTsunami is built to help you upload an English video, generate Spanish subtitles, and download a subtitled version of the video.",
    },
    {
      question: "Is SubtitleTsunami useful for social media videos?",
      answer:
        "Yes. SubtitleTsunami is useful for creators making videos for YouTube Shorts, TikTok, Instagram Reels, and other short-form video platforms.",
    },
    {
      question: "Do I need to manually edit every subtitle line?",
      answer:
        "No. SubtitleTsunami is designed to automate the subtitle generation process so creators can get a Spanish-subtitled video faster.",
    },
    {
      question: "Can I try SubtitleTsunami for free?",
      answer:
        "Yes. SubtitleTsunami offers a free daily upload, with a Starter plan available for users who need more videos per day.",
    },
  ];

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": `${pageUrl}#webpage`,
        name: page.metaTitle,
        description: page.metaDescription,
        url: pageUrl,
        isPartOf: {
          "@type": "WebSite",
          "@id": `${SITE_URL}/#website`,
          name: "SubtitleTsunami",
          url: `${SITE_URL}/`,
        },
        about: {
          "@type": "SoftwareApplication",
          "@id": `${SITE_URL}/#softwareapplication`,
          name: "SubtitleTsunami",
          applicationCategory: "MultimediaApplication",
          operatingSystem: "Web",
          url: `${SITE_URL}/`,
        },
      },
      {
        "@type": "FAQPage",
        "@id": `${pageUrl}#faq`,
        mainEntity: faqItems.map((item) => ({
          "@type": "Question",
          name: item.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: item.answer,
          },
        })),
      },
    ],
  };

  return (
    <>
      <SEO
        title={page.metaTitle}
        description={page.metaDescription}
        canonical={pageUrl}
        image={`${SITE_URL}/og-image.png`}
        type="website"
        jsonLd={jsonLd}
      />

      <main className="min-h-screen bg-slate-950 px-4 py-12 text-white">
        <article className="mx-auto max-w-5xl">
          <section className="rounded-3xl border border-cyan-300/20 bg-slate-900/80 p-6 shadow-2xl shadow-cyan-950/30 md:p-10">
            <p className="mb-4 text-sm font-bold uppercase tracking-[0.3em] text-cyan-300">
              AI Spanish Subtitles
            </p>

            <h1 className="mb-5 text-4xl font-black leading-tight text-white md:text-6xl">
              English to Spanish Subtitle Generator for Videos
            </h1>

            <p className="mb-8 max-w-3xl text-lg leading-8 text-slate-300">
              SubtitleTsunami helps creators turn English videos into
              Spanish-subtitled videos. Upload your video, generate Spanish
              subtitles, and download a finished version you can use for social
              media, marketing, education, or client content.
            </p>

            <div className="flex flex-col gap-3 sm:flex-row">
              <Link
                to="/"
                className="inline-flex items-center justify-center rounded-2xl bg-cyan-300 px-6 py-3 text-base font-black text-slate-950 shadow-lg shadow-cyan-950/40 transition hover:-translate-y-0.5 hover:bg-cyan-200"
              >
                Generate Spanish Subtitles
              </Link>

              <a
                href="#how-it-works"
                className="inline-flex items-center justify-center rounded-2xl border border-white/15 px-6 py-3 text-base font-bold text-white transition hover:bg-white/10"
              >
                See How It Works
              </a>
            </div>
          </section>

          <section id="how-it-works" className="mt-12 grid gap-6 md:grid-cols-3">
            <div className="rounded-3xl border border-white/10 bg-slate-900 p-6">
              <p className="mb-3 text-3xl font-black text-cyan-300">1</p>
              <h2 className="mb-3 text-xl font-black text-white">
                Upload your English video
              </h2>
              <p className="leading-7 text-slate-300">
                Start with the video you already have. SubtitleTsunami is made
                for creators who want a faster path from English video to
                Spanish-subtitled output.
              </p>
            </div>

            <div className="rounded-3xl border border-white/10 bg-slate-900 p-6">
              <p className="mb-3 text-3xl font-black text-cyan-300">2</p>
              <h2 className="mb-3 text-xl font-black text-white">
                Generate Spanish subtitles
              </h2>
              <p className="leading-7 text-slate-300">
                SubtitleTsunami uses AI to help create Spanish subtitles from
                your English video, reducing the amount of manual caption work.
              </p>
            </div>

            <div className="rounded-3xl border border-white/10 bg-slate-900 p-6">
              <p className="mb-3 text-3xl font-black text-cyan-300">3</p>
              <h2 className="mb-3 text-xl font-black text-white">
                Download your subtitled video
              </h2>
              <p className="leading-7 text-slate-300">
                Once processing finishes, download your video with Spanish
                subtitles and use it for your audience, clients, or social
                channels.
              </p>
            </div>
          </section>

          <SeoCTA />

          <section className="mt-12 rounded-3xl border border-white/10 bg-slate-900 p-6 md:p-10">
            <h2 className="mb-5 text-3xl font-black text-white">
              Why use an English to Spanish subtitle generator?
            </h2>

            <div className="space-y-5 text-base leading-8 text-slate-300">
              <p>
                Manually translating, timing, formatting, and burning subtitles
                into a video can take a long time. An English to Spanish subtitle
                generator helps speed up that process so creators can publish
                more videos without getting stuck editing captions line by line.
              </p>

              <p>
                Spanish subtitles can also make an English video easier to
                understand for Spanish-speaking viewers. That can help creators,
                educators, businesses, and marketers reuse one video for a wider
                audience.
              </p>

              <p>
                SubtitleTsunami is focused on a simple workflow: upload your
                video, generate Spanish subtitles, and download the finished
                subtitled version.
              </p>
            </div>
          </section>

          <section className="mt-12 rounded-3xl border border-white/10 bg-slate-900 p-6 md:p-10">
            <h2 className="mb-6 text-3xl font-black text-white">
              Best uses for SubtitleTsunami
            </h2>

            <div className="grid gap-4 md:grid-cols-2">
              {[
                "YouTube Shorts",
                "TikTok videos",
                "Instagram Reels",
                "Educational videos",
                "Marketing videos",
                "Client videos",
                "Course videos",
                "Social media clips",
              ].map((item) => (
                <div
                  key={item}
                  className="rounded-2xl border border-cyan-300/10 bg-slate-950 p-4 text-slate-200"
                >
                  {item}
                </div>
              ))}
            </div>
          </section>

          <section className="mt-12 rounded-3xl border border-white/10 bg-slate-900 p-6 md:p-10">
            <h2 className="mb-6 text-3xl font-black text-white">
              Frequently asked questions
            </h2>

            <div className="space-y-5">
              {faqItems.map((item) => (
                <div
                  key={item.question}
                  className="rounded-2xl border border-white/10 bg-slate-950 p-5"
                >
                  <h3 className="mb-2 text-lg font-black text-white">
                    {item.question}
                  </h3>
                  <p className="leading-7 text-slate-300">{item.answer}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-12 rounded-3xl border border-white/10 bg-slate-900 p-6 md:p-10">
            <h2 className="mb-4 text-2xl font-black text-white">
              Related guides
            </h2>

            <div className="grid gap-4 md:grid-cols-2">
              <Link
                to="/how-to-add-spanish-subtitles-to-video"
                className="rounded-2xl border border-white/10 bg-slate-950 p-5 text-slate-200 transition hover:border-cyan-300/40 hover:bg-slate-900"
              >
                How to Add Spanish Subtitles to an English Video
              </Link>

              <Link
                to="/translate-youtube-shorts-to-spanish-subtitles"
                className="rounded-2xl border border-white/10 bg-slate-950 p-5 text-slate-200 transition hover:border-cyan-300/40 hover:bg-slate-900"
              >
                How to Translate YouTube Shorts into Spanish Subtitles
              </Link>
            </div>
          </section>

          <SeoCTA
            title="Ready to create Spanish subtitles?"
            description="Upload your video and generate English-to-Spanish subtitles with SubtitleTsunami."
            buttonText="Start Free Upload"
          />
        </article>
      </main>
    </>
  );
};

export default EnglishToSpanishSubtitleGenerator;